import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, NgModule } from '@angular/core';
import { Gesture, GestureController, GestureDetail } from '@ionic/angular';
import { IInventoryEvent } from '@shared/interfaces/inventory-event.interface';

/**
 * Recognises "tap" and "long-press" on inventory tiles inside a single host
 * container, with event delegation (the tile is resolved per interaction via
 * {@link tagName}). Public contract is unchanged (`tagName` in, `touchStart` /
 * `touchEnd` out) so consumers stay untouched.
 *
 * Split of responsibilities, chosen for robustness:
 * - The **tap** is detected from a raw `click` ({@link onTapClick}). Ionic's
 *   GestureController is a pan recogniser subordinate to a single *global*
 *   capture (`capturedId`): a tap that coincides with another captured gesture
 *   is silently dropped (its pointer-down bails before any pointer-up handler).
 *   A raw `click` is immune to that arbitration, and the browser already
 *   suppresses `click` after a scroll/drag — so it never fires a false tap.
 * - The **long-press** is detected with the Ionic gesture: {@link onPressStart}
 *   arms a timer on pointer-down (when the gesture captured), {@link onPressMove}
 *   cancels it on a scroll. A long-press suppresses the trailing tap click.
 *
 * Tile resolution uses `closest(tagName)` so a tap on a child node — or on a
 * tile shifted by the cart's add animation — still resolves to the tile.
 */
@Directive({
  selector: '[inventoryEvents]',
})
export class InventoryEventsDirective implements OnInit, OnDestroy {
  /** Tag name (case-insensitive) of the tile element a pointer must resolve to. */
  @Input() tagName: string;
  /** Emitted on a long-press (held without moving). Carries the tile dataset. */
  @Output() touchStart = new EventEmitter<IInventoryEvent>();
  /** Emitted on a tap. Carries the tile dataset and the resolved tile element. */
  @Output() touchEnd = new EventEmitter<IInventoryEvent>();

  /** Max pointer travel (px) before a press is treated as a scroll/drag and cancelled. */
  private readonly maxDistance = 23;
  /** Hold duration (ms) without movement that triggers a long-press. */
  private readonly longPressMs = 500;

  /** The Ionic gesture used for long-press detection (single instance, destroyed on teardown). */
  private gesture?: Gesture;
  /** Handle of the pending long-press timer, or undefined when none is armed. */
  private pressTimerId?: ReturnType<typeof setTimeout>;
  /** Tile element of the in-flight press cycle, or null when none is active. */
  private activeTarget: HTMLElement | null = null;
  /** True once a long-press fired, used to suppress the trailing tap click. */
  private longPressFired = false;
  /** Removes the raw pointerdown/click listeners on teardown. */
  private removeRawListeners?: () => void;

  constructor(private gestureController: GestureController, private host: ElementRef, private zone: NgZone) {}

  /**
   * Wires the long-press gesture and the raw tap listeners. Everything is set up
   * inside `runOutsideAngular`: zone.js captures the zone at listener
   * registration, so the callbacks run out of the Angular zone (perf on a hot
   * scrolling list). `passive: true` keeps native virtual-scroll unblocked and
   * leaves the browser free to synthesise the tap `click`.
   */
  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.gesture = this.gestureController.create({
        el: this.host.nativeElement,
        gestureName: 'inventory-events',
        gesturePriority: 0,
        threshold: 0,
        passive: true,
        onStart: (detail) => this.onPressStart(detail),
        onMove: (detail) => this.onPressMove(detail),
        onEnd: () => this.clearPressTimer(),
      });
      this.gesture.enable();

      const el = this.host.nativeElement as HTMLElement;
      const onPointerDown = () => (this.longPressFired = false);
      const onClick = (event: Event) => this.onTapClick(event);
      el.addEventListener('pointerdown', onPointerDown, true);
      el.addEventListener('click', onClick, true);
      this.removeRawListeners = () => {
        el.removeEventListener('pointerdown', onPointerDown, true);
        el.removeEventListener('click', onClick, true);
      };
    });
  }

  /** Tears down the gesture, the raw listeners and any armed timer so nothing leaks. */
  ngOnDestroy(): void {
    this.clearPressTimer();
    this.gesture?.destroy();
    this.gesture = undefined;
    this.removeRawListeners?.();
    this.removeRawListeners = undefined;
    this.activeTarget = null;
  }

  /** Gesture start: arm the long-press timer for the resolved tile (when the gesture captured). */
  private onPressStart(detail: GestureDetail): void {
    const tile = this.resolveTile(detail.event.target as HTMLElement);
    if (!tile) {
      this.activeTarget = null;
      return;
    }
    this.activeTarget = tile;
    this.pressTimerId = this.zone.runOutsideAngular(() => setTimeout(() => this.emitLongPress(), this.longPressMs));
  }

  /** Gesture move: a scroll/drag beyond the threshold cancels the pending long-press. */
  private onPressMove(detail: GestureDetail): void {
    if (this.activeTarget && this.distance(detail) > this.maxDistance) {
      this.cancelPress();
    }
  }

  /**
   * Raw click: the authoritative, capture-immune tap. Suppressed once after a
   * long-press (a long-press still produces a trailing click on release).
   */
  private onTapClick(event: Event): void {
    if (this.longPressFired) {
      this.longPressFired = false;
      return;
    }
    const tile = this.resolveTile(event.target as HTMLElement);
    if (tile) {
      this.touchEnd.emit({ dataset: tile.dataset, eventTarget: tile });
    }
  }

  /** Fired by the timer: emits the long-press and marks the cycle so no tap follows. */
  private emitLongPress(): void {
    const tile = this.activeTarget;
    if (!tile) {
      return;
    }
    this.longPressFired = true;
    this.activeTarget = null;
    this.touchStart.emit({ dataset: tile.dataset });
  }

  /** Aborts the active press (scroll detected): disarms the timer and forgets the tile. */
  private cancelPress(): void {
    this.clearPressTimer();
    this.activeTarget = null;
  }

  /** Clears the long-press timer if one is armed. Idempotent. */
  private clearPressTimer(): void {
    if (this.pressTimerId !== undefined) {
      clearTimeout(this.pressTimerId);
      this.pressTimerId = undefined;
    }
  }

  /** Resolves the inventory tile from an event target, tolerant of child nodes / shifted layout. */
  private resolveTile(target: HTMLElement | null): HTMLElement | null {
    const tag = this.tagName?.toLowerCase();
    if (!target || !tag) {
      return null;
    }
    return target.closest(tag) as HTMLElement | null;
  }

  /** Euclidean pointer travel since gesture start, from Ionic's normalised deltas. */
  private distance(detail: GestureDetail): number {
    return Math.hypot(detail.deltaX, detail.deltaY);
  }
}
@NgModule({
  declarations: [InventoryEventsDirective],
  exports: [InventoryEventsDirective],
})
export class InventoryEventsDirectiveModule {}
